import { Icon } from "@circle-react-shared/Icon";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { useQuery } from "react-query";

import { ConnectButton } from "@/react/components/ConnectionButtons/ConnectButton";
import { ConnectedButton } from "@/react/components/ConnectionButtons/ConnectedButton";
import { useConnectionButtonState } from "@/react/components/ConnectionButtons/hooks/useConnectionButtonState";
import { MessageButton } from "@/react/components/ConnectionButtons/MessageButton";
import { ViewProfileButton } from "@/react/components/ConnectionButtons/ViewProfileButton";
import { useCanMessageMember } from "@/react/components/Profile/useCanMessageMember";
import { Button } from "@/react/components/SettingsApp/CompassCompat/Button";
import { PROFILE_PAGES } from "@/react/helpers/memberHelper";
import { classNames } from "@/react/helpers/twMergeWithCN";
import {
  useProfileInfo,
  type ProfileResponse,
} from "@/react/hooks/profile/useProfileInfo";
import { useCircleV4 } from "@/react/hooks/useCircleV4";
import {
  useCanCreateConnection,
  useIsConnectEnabled,
} from "@/react/hooks/useIsConnectEnabled";

interface ActionButtonsProps {
  closeCard: () => void;
  communityMemberId: number | string;
  messagingEnabled?: boolean;
  publicUid?: string;
  sqid?: string;
}

const params = {
  page_name: PROFILE_PAGES.PROFILE_VIEW,
};

export const ActionButtons = ({
  communityMemberId,
  publicUid,
  sqid,
  messagingEnabled,
  closeCard,
}: ActionButtonsProps) => {
  const { currentCommunityMember } = usePunditUserContext();
  const { shouldShowMessageOption } = useCanMessageMember();
  const isCurrentCommunityMember =
    currentCommunityMember?.id == communityMemberId;

  const { isCircleV4 } = useCircleV4();

  const shouldFetchByPublicUid = !isCurrentCommunityMember && !!publicUid;
  const shouldFetchBySqid = !isCurrentCommunityMember && !publicUid && !!sqid;

  const { getProfileInfoQuery } = useProfileInfo({
    publicId: shouldFetchByPublicUid ? publicUid : undefined,
    params,
  });

  const contactQuery = useQuery<ProfileResponse>({
    queryKey: ["contact_profile", sqid, params],
    queryFn: () =>
      reactQueryGet(
        internalApi.profile.showBySqid({ sqid: sqid ?? "", params }),
      ),
    retry: false,
    enabled: shouldFetchBySqid,
  });

  const isConnectEnabled = useIsConnectEnabled();
  const canCreateConnection = useCanCreateConnection();
  const profile = shouldFetchBySqid
    ? contactQuery.data
    : getProfileInfoQuery.data;
  const isProfileLoading = shouldFetchBySqid
    ? contactQuery.isInitialLoading
    : getProfileInfoQuery.isInitialLoading;

  const { isActive, isEmpty } = useConnectionButtonState(profile);

  const shouldShowMessageButton =
    !isCurrentCommunityMember &&
    messagingEnabled &&
    !isConnectEnabled &&
    shouldShowMessageOption(profile as any);

  const hasAnythingToRender =
    shouldShowMessageButton || isConnectEnabled || !!profile;
  if (!hasAnythingToRender) return null;

  return (
    <div
      className={classNames("flex w-full *:flex-1", {
        "gap-3": isCircleV4,
        "mt-4 space-x-2": !isCircleV4,
      })}
    >
      {/* When connect is disabled we go back to default behaviour which is showing message button */}
      {!isConnectEnabled && shouldShowMessageButton && (
        <MessageButton memberId={communityMemberId} closeParent={closeCard} />
      )}

      {isConnectEnabled && (
        <>
          {!isCurrentCommunityMember && isProfileLoading && (
            <Button
              variant="secondary"
              disabled
              className="!border-primary !h-8 w-full gap-2 !px-4 !font-medium"
            >
              <Icon type="loader" />
            </Button>
          )}
          {isEmpty && canCreateConnection && profile && (
            <ConnectButton
              recipient={profile}
              className="flex-1"
              buttonVariant="circle"
              onClick={closeCard}
            />
          )}
          {!isProfileLoading && isActive && (
            <ConnectedButton
              className="!bg-secondary border-0 flex-1"
              compassClassName="flex-1"
            />
          )}
        </>
      )}

      {profile && (
        <ViewProfileButton
          member={profile}
          className="flex-1"
          onProfileOpen={closeCard}
        />
      )}
    </div>
  );
};
